import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPackage, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { useCompras } from '../../hooks/useCompras';

const CompraRecibir = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { getOrdenById, recibirOrden } = useCompras();
  
  const [orden, setOrden] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [receiptData, setReceiptData] = useState({
    receipt_date: new Date().toISOString().split('T')[0],
    notes: '',
    items: []
  });

  useEffect(() => {
    loadOrdenData();
  }, [id]);

  const loadOrdenData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getOrdenById(id);
      
      if (result.success) {
        setOrden(result.data);
        // Inicializar los datos de recepción
        setReceiptData(prev => ({
          ...prev,
          items: (result.data.items || []).map(item => ({
            item_id: item.id,
            product_id: item.product_id,
            product_name: item.product_name || item.description,
            ordered_quantity: item.quantity,
            received_quantity: 0,
            remaining_quantity: item.quantity - (item.received_quantity || 0),
            unit_cost: item.unit_cost,
            condition: 'GOOD', // GOOD, DAMAGED, DEFECTIVE
            batch_number: '',
            expiration_date: '',
            notes: ''
          }))
        }));
      } else {
        setError(result.error || 'Orden de compra no encontrada');
      }
    } catch (error) {
      setError('Error de conexión al cargar orden de compra');
      console.error('Error loading purchase order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateReceiptItem = (index, field, value) => {
    setReceiptData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index 
          ? { 
              ...item, 
              [field]: field === 'received_quantity' 
                ? Math.max(0, Math.min(parseFloat(value) || 0, item.remaining_quantity))
                : value
            }
          : item
      )
    }));
  };

  const updateReceiptData = (field, value) => {
    setReceiptData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTotalReceived = () => {
    return receiptData.items.reduce((sum, item) => sum + (item.received_quantity || 0), 0);
  };

  const getTotalValue = () => {
    return receiptData.items.reduce((sum, item) => 
      sum + ((item.received_quantity || 0) * (item.unit_cost || 0)), 0
    );
  };

  const hasItemsToReceive = () => {
    return receiptData.items.some(item => item.received_quantity > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasItemsToReceive()) {
      setError('Debe especificar al menos una cantidad recibida');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Filtrar solo los items que tienen cantidad recibida
      const itemsToReceive = receiptData.items.filter(item => item.received_quantity > 0);
      
      const result = await recibirOrden(id, {
        ...receiptData,
        items: itemsToReceive
      });
      
      if (result.success) {
        navigate(`/system/compras/${id}`);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Error al recibir productos');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const getConditionLabel = (condition) => {
    const conditions = {
      GOOD: 'Bueno',
      DAMAGED: 'Dañado',
      DEFECTIVE: 'Defectuoso'
    };
    return conditions[condition] || condition;
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando orden de compra..." />;
  }

  if (error && !orden) {
    return (
      <div className="error-page">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            className="btn btn--primary"
            onClick={() => navigate('/system/compras')}
          >
            Volver a Órdenes de Compra
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="compra-recibir-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button 
            className="btn btn--ghost btn--sm"
            onClick={() => navigate(`/system/compras/${id}`)}
          >
            <FiArrowLeft /> Volver
          </button>
          <div className="page-title-section">
            <h1>Recibir Productos</h1>
            <div className="page-subtitle">
              Orden: {orden.order_number} - {orden.supplier_name}
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            type="button" 
            className="btn btn--ghost"
            onClick={() => navigate(`/system/compras/${id}`)}
          >
            <FiX /> Cancelar
          </button>
          <button 
            type="submit" 
            form="receipt-form"
            className="btn btn--primary"
            disabled={isSaving || !hasItemsToReceive()}
          >
            <FiSave /> {isSaving ? 'Guardando...' : 'Registrar Recepción'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Resumen */}
      <div className="receipt-summary">
        <div className="summary-card">
          <div className="summary-item">
            <div className="summary-label">Items a Recibir</div>
            <div className="summary-value">{getTotalReceived()}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Valor Total</div>
            <div className="summary-value">{formatCurrency(getTotalValue())}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Estado</div>
            <div className="summary-value">
              {hasItemsToReceive() ? (
                <span className="status-ready">
                  <FiCheck /> Listo para recibir
                </span>
              ) : (
                <span className="status-pending">
                  <FiAlertCircle /> Especifique cantidades
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <form id="receipt-form" onSubmit={handleSubmit} className="receipt-form">
        <div className="form-section">
          <h3>Información de Recepción</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="receipt_date">Fecha de Recepción *</label>
              <input
                type="date"
                id="receipt_date"
                value={receiptData.receipt_date}
                onChange={(e) => updateReceiptData('receipt_date', e.target.value)}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="notes">Notas de Recepción</label>
            <textarea
              id="notes"
              value={receiptData.notes}
              onChange={(e) => updateReceiptData('notes', e.target.value)}
              rows={3}
              placeholder="Notas sobre la recepción de productos..."
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Productos a Recibir</h3>
          
          <div className="receipt-table">
            <div className="table-header">
              <div className="th">Producto</div>
              <div className="th">Ordenado</div>
              <div className="th">Pendiente</div>
              <div className="th">Recibir</div>
              <div className="th">Condición</div>
              <div className="th">Lote/Venc.</div>
              <div className="th">Notas</div>
            </div>
            
            {receiptData.items.map((item, index) => (
              <div key={index} className="table-row">
                <div className="td">
                  <div className="product-info">
                    <div className="product-name">{item.product_name}</div>
                    <div className="product-price">
                      {formatCurrency(item.unit_cost)} c/u
                    </div>
                  </div>
                </div>
                
                <div className="td">
                  <span className="quantity-badge">{item.ordered_quantity}</span>
                </div>
                
                <div className="td">
                  <span className="quantity-badge quantity-badge--pending">
                    {item.remaining_quantity}
                  </span>
                </div>
                
                <div className="td">
                  <input
                    type="number"
                    value={item.received_quantity}
                    onChange={(e) => updateReceiptItem(index, 'received_quantity', e.target.value)}
                    min="0"
                    max={item.remaining_quantity}
                    step="0.01"
                    className="form-control form-control--sm"
                    placeholder="0"
                  />
                </div>
                
                <div className="td">
                  <select
                    value={item.condition}
                    onChange={(e) => updateReceiptItem(index, 'condition', e.target.value)}
                    className="form-control form-control--sm"
                  >
                    <option value="GOOD">Bueno</option>
                    <option value="DAMAGED">Dañado</option>
                    <option value="DEFECTIVE">Defectuoso</option>
                  </select>
                </div>
                
                <div className="td">
                  <div className="batch-inputs">
                    <input
                      type="text"
                      value={item.batch_number}
                      onChange={(e) => updateReceiptItem(index, 'batch_number', e.target.value)}
                      placeholder="Lote"
                      className="form-control form-control--xs"
                    />
                    <input
                      type="date"
                      value={item.expiration_date}
                      onChange={(e) => updateReceiptItem(index, 'expiration_date', e.target.value)}
                      className="form-control form-control--xs"
                    />
                  </div>
                </div>
                
                <div className="td">
                  <input
                    type="text"
                    value={item.notes}
                    onChange={(e) => updateReceiptItem(index, 'notes', e.target.value)}
                    placeholder="Notas..."
                    className="form-control form-control--sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Resumen de totales */}
        <div className="form-section">
          <div className="receipt-totals">
            <div className="total-row">
              <span>Total de Items Recibidos:</span>
              <span className="total-value">{getTotalReceived()}</span>
            </div>
            <div className="total-row">
              <span>Valor Total Recibido:</span>
              <span className="total-value">{formatCurrency(getTotalValue())}</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompraRecibir;
